/**
 * Sacred Calendar
 *
 * Moon phase algorithm
 * - Reference new moon: 2000-01-06 18:14 UTC
 * - Synodic month: 29.53058867 days
 * - Age = ((unixMs - knownNewMs) / 86400000) mod synodic
 * - Illumination % ≈ (1 - cos(2π * age / synodic)) / 2 * 100
 * - Phase name from age buckets (new, crescents, quarters, gibbous, full)
 * Computed only for the visible ~42 cells of the current month view. No APIs.
 *
 * Metaobject field mapping (type handle: sacred_calendar_event)
 * - title            single line text
 * - event_type       moon | retrograde | sabbat | santa_muerte | dia_de_muertos | other
 * - start_date       date (optional)
 * - end_date         date (optional, multi-day)
 * - recurrence       e.g. weekly:tuesday
 * - description      multi-line / rich text
 * - color_token      void | obsidian | crypt | bone | ash | sangre | relic | ...
 * - icon             optional file
 *
 * Punch list before live
 * - 2026–2027 Mercury/Venus (and other) retrograde windows — dates not invented
 * - Eclipses and other planetary events Kimmy wants tracked
 * - Four solstice/equinox dates (Yule, Ostara, Litha, Mabon) vs ephemeris
 * - Confirm four cross-quarter sabbat dates (Imbolc, Beltane, Lughnasadh, Samhain)
 * - Santa Muerte Tuesday description (left empty on purpose)
 * - Never cite Andrew Chestnut. Santa Muerte feast copy stays devotee-plain only.
 */
(function () {
  const SYNODIC = 29.53058867;
  const KNOWN_NEW_MS = Date.UTC(2000, 0, 6, 18, 14, 0);
  const PHASES = [
    { max: 1.84566, name: 'New Moon' },
    { max: 5.53699, name: 'Waxing Crescent' },
    { max: 9.22831, name: 'First Quarter' },
    { max: 12.91963, name: 'Waxing Gibbous' },
    { max: 16.61096, name: 'Full Moon' },
    { max: 20.30228, name: 'Waning Gibbous' },
    { max: 23.99361, name: 'Last Quarter' },
    { max: 27.68493, name: 'Waning Crescent' },
    { max: 29.53059, name: 'New Moon' }
  ];

  const WEEKDAY_RECURRENCE = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };
  const SANTA_MUERTE_WEEKDAY = 2;
  const WEEKDAY_PLANET_EN = ['the Sun', 'the Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const WEEKDAY_PLANET_ES = ['el Sol', 'la Luna', 'Marte', 'Mercurio', 'Júpiter', 'Venus', 'Saturno'];

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function toIsoDate(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function parseIsoDate(value) {
    if (!value) return null;
    const parts = String(value).slice(0, 10).split('-');
    if (parts.length < 3) return null;
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }

  function moonForDate(date) {
    const noon = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
    let days = (noon - KNOWN_NEW_MS) / 86400000;
    let age = days % SYNODIC;
    if (age < 0) age += SYNODIC;
    const illumination = ((1 - Math.cos((2 * Math.PI * age) / SYNODIC)) / 2) * 100;
    let phase = PHASES[PHASES.length - 1];
    for (let i = 0; i < PHASES.length; i++) {
      if (age < PHASES[i].max) {
        phase = PHASES[i];
        break;
      }
    }
    return {
      age: age,
      name: phase.name,
      illumination: Math.round(illumination),
      waxing: age < SYNODIC / 2
    };
  }

  function isSpanish() {
    return String(document.documentElement.lang || '').toLowerCase().indexOf('es') === 0;
  }

  function sacredToLabel(date) {
    if (date.getDay() === SANTA_MUERTE_WEEKDAY) return 'Santa Muerte';
    const planets = isSpanish() ? WEEKDAY_PLANET_ES : WEEKDAY_PLANET_EN;
    return planets[date.getDay()];
  }

  function nextSantaMuerteTuesday(from) {
    const d = startOfDay(from);
    const delta = (SANTA_MUERTE_WEEKDAY - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + delta);
    return d;
  }

  function formatSantaMuerteObservance(date) {
    const formatted = new Intl.DateTimeFormat(isSpanish() ? 'es-MX' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    }).format(date);
    return isSpanish()
      ? 'Santa Muerte — Día sagrado · ' + formatted
      : 'Santa Muerte — Sacred Day · ' + formatted;
  }

  function moonSrc(map, moon) {
    if (!map) return '';
    return map[moon.name] || map['Full Moon'] || '';
  }

  function decodeHtml(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value || '');
    return textarea.value;
  }

  function moonImg(map, moon) {
    const src = moonSrc(map, moon);
    if (!src) return '';
    return (
      '<img class="calendar-moon" src="' +
      src +
      '" alt="" width="22" height="22" decoding="async" aria-hidden="true">'
    );
  }

  function normalizeEvents(raw) {
    const list = Array.isArray(raw) ? raw : raw && raw.events ? raw.events : [];
    return list.map(function (event) {
      const category = event.category || event.event_type || 'other';
      return {
        title: event.title || '',
        subtitle: event.subtitle || '',
        category: category,
        event_type: event.event_type || category,
        start_date: event.start_date || null,
        end_date: event.end_date || null,
        recurrence: event.recurrence || '',
        description: event.description || '',
        color_token: event.color_token || 'relic',
        icon: event.icon || null,
        needs_verification: Boolean(event.needs_verification)
      };
    });
  }

  function recurrenceMatches(event, date) {
    const rec = String(event.recurrence || '').toLowerCase();
    if (!rec) return false;
    if (rec.indexOf('weekly:') === 0) {
      const day = rec.split(':')[1];
      return date.getDay() === WEEKDAY_RECURRENCE[day];
    }
    if (rec.indexOf('yearly:') === 0) {
      const mmdd = rec.split(':')[1];
      return pad(date.getMonth() + 1) + '-' + pad(date.getDate()) === mmdd;
    }
    return false;
  }

  function eventOnDate(event, date) {
    if (recurrenceMatches(event, date)) return true;
    const start = parseIsoDate(event.start_date);
    if (!start) return false;
    const end = parseIsoDate(event.end_date) || start;
    const t = startOfDay(date).getTime();
    return t >= startOfDay(start).getTime() && t <= startOfDay(end).getTime();
  }

  function eventsForDate(events, date) {
    return events.filter(function (event) {
      return eventOnDate(event, date);
    });
  }

  function isMultiDay(event) {
    return Boolean(event.start_date && event.end_date && event.start_date !== event.end_date);
  }

  function tokenClass(token) {
    return 'calendar-dot--' + String(token || 'relic').replace(/_/g, '-');
  }

  function uniqueTypes(dayEvents) {
    const seen = {};
    const dots = [];
    dayEvents.forEach(function (event) {
      if (event.event_type === 'moon') return;
      const key = event.event_type + ':' + event.color_token;
      if (seen[key]) return;
      seen[key] = true;
      dots.push(event);
    });
    return dots.slice(0, 3);
  }

  function monthGrid(year, month) {
    const first = new Date(year, month, 1);
    const startOffset = first.getDay();
    const start = new Date(year, month, 1 - startOffset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      cells.push(date);
    }
    return cells;
  }

  function currentWeekIndex(cells, today) {
    const iso = toIsoDate(today);
    for (let i = 0; i < cells.length; i++) {
      if (toIsoDate(cells[i]) === iso) return Math.floor(i / 7);
    }
    return 0;
  }

  function getFocusable(modal) {
    return Array.prototype.slice.call(
      modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden');
    });
  }

  function SacredCalendar(root) {
    this.root = root;
    this.grid = root.querySelector('[data-calendar-grid]');
    this.monthLabel = root.querySelector('[data-calendar-month]');
    this.modal = root.querySelector('.calendar-modal');
    this.dayTemplate = root.querySelector('[data-day-template]');
    this.strings = JSON.parse(root.querySelector('[data-calendar-strings]').textContent);
    this.day = 0;
    this.syncFromClock();
    this.view = window.matchMedia('(max-width: 479px)').matches ? 'week' : 'month';
    this.weekIndex = 0;
    this.events = [];
    this.cells = [];
    this.lastTrigger = null;
    this.monthTimer = 0;
    this.bind();
    this.watchMonthRollover();
  }

  SacredCalendar.prototype.bind = function () {
    const self = this;
    const weekBtn = this.root.querySelector('[data-calendar-week]');
    const monthBtn = this.root.querySelector('[data-calendar-month-view]');
    if (weekBtn) {
      weekBtn.addEventListener('click', function () {
        self.view = 'week';
        self.render();
      });
    }
    if (monthBtn) {
      monthBtn.addEventListener('click', function () {
        self.view = 'month';
        self.render();
      });
    }
    this.grid.addEventListener('keydown', function (event) {
      self.onGridKey(event);
    });
    this.modal.querySelectorAll('[data-calendar-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.closeModal();
      });
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !self.modal.hidden) {
        self.closeModal();
      }
    });
  };

  SacredCalendar.prototype.syncFromClock = function () {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    const changed = year !== this.year || month !== this.month || day !== this.day;
    this.year = year;
    this.month = month;
    this.day = day;
    return changed;
  };

  SacredCalendar.prototype.watchMonthRollover = function () {
    const self = this;
    const schedule = function () {
      window.clearTimeout(self.monthTimer);
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 1);
      const wait = Math.max(1000, Math.min(nextMonth.getTime() - now.getTime(), 12 * 60 * 60 * 1000));
      self.monthTimer = window.setTimeout(function () {
        if (self.syncFromClock()) self.render();
        schedule();
      }, wait);
    };
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && self.syncFromClock()) self.render();
    });
    schedule();
  };

  SacredCalendar.prototype.load = function (events) {
    this.events = normalizeEvents(events);
    this.render();
  };

  SacredCalendar.prototype.renderAltarStrip = function () {
    const moonEl = this.root.querySelector('[data-altar-moon]');
    const sacredEl = this.root.querySelector('[data-altar-sacred]');
    const nextEl = this.root.querySelector('[data-altar-next]');
    if (!moonEl || !sacredEl || !nextEl) return;
    const today = startOfDay(new Date());
    const moon = moonForDate(today);
    moonEl.textContent = moon.name;
    const moonImage = this.root.querySelector('[data-altar-moon-image]');
    const src = moonSrc(this.strings.moons, moon);
    if (moonImage && src) {
      moonImage.src = src;
      moonImage.hidden = false;
    }
    sacredEl.textContent = sacredToLabel(today);
    nextEl.textContent = formatSantaMuerteObservance(nextSantaMuerteTuesday(today));
  };

  SacredCalendar.prototype.render = function () {
    const self = this;
    const today = startOfDay(new Date());
    this.renderAltarStrip();
    this.cells = monthGrid(this.year, this.month);
    this.weekIndex = currentWeekIndex(this.cells, today);
    this.monthLabel.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      month: 'long',
      year: 'numeric'
    }).format(new Date(this.year, this.month, 1));

    this.root.classList.toggle('is-week', this.view === 'week');
    const weekBtn = this.root.querySelector('[data-calendar-week]');
    const monthBtn = this.root.querySelector('[data-calendar-month-view]');
    if (weekBtn) weekBtn.setAttribute('aria-pressed', this.view === 'week' ? 'true' : 'false');
    if (monthBtn) monthBtn.setAttribute('aria-pressed', this.view === 'month' ? 'true' : 'false');

    this.grid.innerHTML = '';
    const tmpl = this.dayTemplate.content;
    this.cells.forEach(function (date, index) {
      const node = tmpl.firstElementChild.cloneNode(true);
      const iso = toIsoDate(date);
      const outside = date.getMonth() !== self.month;
      const moon = moonForDate(date);
      const dayEvents = eventsForDate(self.events, date);
      const dots = uniqueTypes(dayEvents);
      const rangeEvents = dayEvents.filter(isMultiDay);
      const weekRow = Math.floor(index / 7);

      node.dataset.date = iso;
      node.dataset.index = String(index);
      node.querySelector('.calendar-day__num').textContent = String(date.getDate());
      node.querySelector('.calendar-day__moon').innerHTML = moonImg(self.strings.moons, moon);
      node.setAttribute(
        'aria-label',
        new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        }).format(date) +
          ', ' +
          moon.name
      );
      node.classList.toggle('is-outside', outside);
      node.classList.toggle('is-today', iso === toIsoDate(today));
      node.classList.toggle('is-outside-week', weekRow !== self.weekIndex);
      node.tabIndex = index === 0 ? 0 : -1;

      if (rangeEvents.length) {
        node.classList.add('has-range');
        if (rangeEvents[0].color_token === 'sangre') node.classList.add('has-range-sangre');
      }

      const dotsWrap = node.querySelector('.calendar-day__dots');
      dots.forEach(function (event) {
        const dot = document.createElement('span');
        dot.className = 'calendar-dot ' + tokenClass(event.color_token);
        dot.title = event.title;
        dotsWrap.appendChild(dot);
      });

      node.addEventListener('click', function () {
        self.openModal(node, date, moon, dayEvents);
      });

      self.grid.appendChild(node);
    });
  };

  SacredCalendar.prototype.onGridKey = function (event) {
    const keys = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (!(event.key in keys) && event.key !== 'Enter' && event.key !== ' ') return;
    const current = document.activeElement;
    if (!current || !this.grid.contains(current)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      current.click();
      return;
    }
    event.preventDefault();
    const nextIndex = Number(current.dataset.index) + keys[event.key];
    const next = this.grid.querySelector('[data-index="' + nextIndex + '"]');
    if (!next) return;
    current.tabIndex = -1;
    next.tabIndex = 0;
    next.focus();
  };

  SacredCalendar.prototype.openModal = function (trigger, date, moon, dayEvents) {
    this.lastTrigger = trigger;
    const title = this.modal.querySelector('.calendar-modal__title');
    const moonEl = this.modal.querySelector('.calendar-modal__moon');
    const list = this.modal.querySelector('.calendar-modal__events');
    title.id = 'calendar-modal-title-' + this.root.dataset.sectionId;
    this.modal.setAttribute('aria-labelledby', title.id);
    title.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
    const moonImage = this.modal.querySelector('.calendar-modal__moon-image');
    const src = moonSrc(this.strings.moons, moon);
    if (moonImage && src) {
      moonImage.src = src;
      moonImage.alt = moon.name;
      moonImage.hidden = false;
    }
    moonEl.textContent = moon.name;
    const moonUse = this.modal.querySelector('.calendar-modal__moon-use');
    const moonIllum = this.modal.querySelector('.calendar-modal__moon-illum');
    const uses = this.strings.moon_uses || {};
    if (moonUse) moonUse.textContent = decodeHtml(uses[moon.name] || '');
    if (moonIllum) {
      moonIllum.textContent = String(this.strings.illumination).replace(
        /\{\{\s*percent\s*\}\}|__PERCENT__/g,
        String(moon.illumination)
      );
    }
    list.innerHTML = '';
    const labels = this.strings.categories || {};
    dayEvents.forEach(function (event) {
      const item = document.createElement('li');
      const badge = document.createElement('span');
      const category = event.category || event.event_type || 'other';
      badge.className = 'calendar-modal__event-badge calendar-modal__event-badge--' + String(category).replace(/_/g, '-');
      badge.textContent = decodeHtml(labels[category] || category);
      item.appendChild(badge);
      const heading = document.createElement('p');
      heading.className = 'calendar-modal__event-title';
      heading.textContent = event.subtitle ? event.title + ' · ' + event.subtitle : event.title;
      item.appendChild(heading);
      if (event.description) {
        const body = document.createElement('p');
        body.className = 'calendar-modal__event-body';
        body.textContent = event.description;
        item.appendChild(body);
      }
      list.appendChild(item);
    });
    this.modal.hidden = false;
    const focusables = getFocusable(this.modal);
    const closeBtn = this.modal.querySelector('.calendar-modal__close');
    (closeBtn || focusables[0] || this.modal).focus();
    const self = this;
    this.trapHandler = function (event) {
      if (event.key !== 'Tab' || self.modal.hidden) return;
      const items = getFocusable(self.modal);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.trapHandler);
  };

  SacredCalendar.prototype.closeModal = function () {
    this.modal.hidden = true;
    if (this.trapHandler) document.removeEventListener('keydown', this.trapHandler);
    if (this.lastTrigger) this.lastTrigger.focus();
  };

  function boot(root, events) {
    const calendar = new SacredCalendar(root);
    calendar.load(events);
  }

  function parseJson(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return { events: [] };
    }
  }

  function eventsFromPayload(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.events)) return data.events;
    return [];
  }

  document.querySelectorAll('[data-sacred-calendar]').forEach(function (root) {
    const inline = root.querySelector('[data-calendar-events]');
    const extra = inline && inline.textContent.trim() ? eventsFromPayload(parseJson(inline.textContent)) : [];
    const src = root.getAttribute('data-events-src');
    if (!src) {
      boot(root, { events: extra });
      return;
    }
    fetch(src)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        boot(root, { events: eventsFromPayload(data).concat(extra) });
      })
      .catch(function () {
        boot(root, { events: extra });
      });
  });
})();
